import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/** @type {string} */
const CART_NOTE_IDLE_EVENT = 'cart-note:idle';

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {HTMLTextAreaElement | null} */
  #textarea = null;

  connectedCallback() {
    super.connectedCallback();
    this.#textarea = this.querySelector('#cart-note');
    const textarea = this.#textarea;
    if (!(textarea instanceof HTMLTextAreaElement)) return;

    textarea.addEventListener('focus', this.#handleFocus);
    textarea.addEventListener('blur', this.#handleBlur);
  }

  disconnectedCallback() {
    const textarea = this.#textarea;
    if (textarea instanceof HTMLTextAreaElement) {
      textarea.removeEventListener('focus', this.#handleFocus);
      textarea.removeEventListener('blur', this.#handleBlur);
    }
    this.updateCartNote.cancel();
    super.disconnectedCallback();
  }

  #handleFocus = () => {
    this.setAttribute('data-note-active', '');
  };

  #handleBlur = () => {
    this.removeAttribute('data-note-active');
    this.updateCartNote.cancel();

    const textarea = this.#textarea;
    if (!(textarea instanceof HTMLTextAreaElement)) {
      this.#notifyIdle();
      return;
    }

    if (textarea.value.trim() === textarea.defaultValue.trim()) {
      this.#notifyIdle();
      return;
    }

    void this.#saveNote(textarea.value).finally(() => {
      this.#notifyIdle();
    });
  };

  #notifyIdle() {
    this.removeAttribute('data-note-saving');
    document.dispatchEvent(new CustomEvent(CART_NOTE_IDLE_EVENT, { bubbles: true }));
  }

  /**
   * @param {string} note
   */
  async #saveNote(note) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.setAttribute('data-note-saving', '');

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) return;

      const data = await response.json();
      const textarea = this.#textarea;
      if (textarea instanceof HTMLTextAreaElement) {
        textarea.defaultValue = data.note ?? note;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    } finally {
      this.#activeFetch = null;
      this.removeAttribute('data-note-saving');
    }
  }

  /**
   * Handles updates to the cart note.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    const note = event.target.value;

    try {
      await this.#saveNote(note);

      if (document.activeElement !== event.target) {
        this.#notifyIdle();
      }
    } finally {
      try {
        cartPerformance.measureFromEvent('note-update:user-action', event);
      } catch {}
    }
  }, 300);
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
